import { apiClient } from './ApiClient';

export interface FinancialRecord {
  id: number;
  asset_id: string;
  amount: number;
  frequency: string;
  type: 'INCOME' | 'EXPENSE';
  category: string;
  is_percent: boolean;
  notify: boolean;
  due_dates: string[];
  details?: string;
  created_at: string;
  updated_at: string;
}

export interface MissingTransactionsResponse {
  count: number;
  startDate: string;
  endDate: string;
}

export interface FinancialSuggestion {
  category: string;
  type: 'INCOME' | 'EXPENSE';
  suggestedAmount: number;
  suggestedFrequency: string;
  transactionCount: number;
  lastTransactionDate: string;
  firstTransactionDate: string;
  sampleTransactions: Array<{
    id: number;
    name: string;
    amount: number;
    date: string;
  }>;
}

export interface AnalyzeTransactionsResponse {
  suggestions: FinancialSuggestion[];
  totalTransactions: number;
  analyzedCategories: number;
}

export const FinancialService = {
  async getAssetFinancials(assetId: string): Promise<FinancialRecord[]> {
    const response = await apiClient.get(`/assets/${assetId}/financials`);
    return response.data;
  },

  async getFinancial(
    assetId: string,
    financialId: number,
  ): Promise<FinancialRecord> {
    const response = await apiClient.get(
      `/assets/${assetId}/financials/${financialId}`,
    );
    return response.data;
  },

  async createFinancial(
    assetId: string,
    data: Omit<
      FinancialRecord,
      'id' | 'asset_id' | 'created_at' | 'updated_at'
    >,
  ): Promise<FinancialRecord> {
    const response = await apiClient.post(
      `/assets/${assetId}/financials`,
      data,
    );
    return response.data;
  },

  async updateFinancial(
    assetId: string,
    financialId: number,
    data: Partial<FinancialRecord>,
  ): Promise<FinancialRecord> {
    const response = await apiClient.put(
      `/assets/${assetId}/financials/${financialId}`,
      data,
    );
    return response.data;
  },

  async deleteFinancial(assetId: string, financialId: number): Promise<void> {
    await apiClient.delete(`/assets/${assetId}/financials/${financialId}`);
  },

  getMissingTransactions: async (
    assetId: string,
    financialId: number,
    startDate: string,
    endDate: string,
  ): Promise<MissingTransactionsResponse> => {
    const response = await apiClient.get(
      `/assets/${assetId}/financials/${financialId}/missing-transactions`,
      {
        params: { startDate, endDate },
      },
    );
    return response.data;
  },

  backfillTransactions: async (
    assetId: string,
    financialId: number,
    startDate: string,
    endDate: string,
  ): Promise<void> => {
    await apiClient.post(
      `/assets/${assetId}/financials/${financialId}/backfill`,
      { startDate, endDate },
    );
  },

  analyzeTransactionsForFinancials: async (
    assetId: string,
  ): Promise<AnalyzeTransactionsResponse> => {
    const response = await apiClient.get(
      `/assets/${assetId}/financials/analyze-transactions`,
    );
    return response.data;
  },
};

export default FinancialService;
